<template>
  <div v-bind:class="{ spinner: isLoading }">
    <div class="flex-container space-left">
      <div class="font-weight-bold min-width">{{$t('Webportal.AggregationLevel') + ' 1:'}}</div>
      <treeselect instance-id="propertyAggregationTreeselect"
                  ref="propertyAggregationTreeselect"
                  v-model="aggregationValue"
                  :clearable="false"
                  :deleteRemoves="false"
                  :backspaceRemoves="false"
                  :auto-load-root-options="false"
                  :placeholder="$t('Webportal.PropertyFilter.Placeholder')"
                  :options="aggregationItems"
                  :disableFuzzyMatching="true"
                  :multiple="false"
                  :showCount="true"
                  showCountOf="LEAF_DESCENDANTS"
                  :defaultExpandLevel="1"
                  :disable-branch-nodes="true"
                  style="max-width: 20em; margin-left: 10px;align-content: start;"
                  @input="handleTreeselectInput" 
                  @close="aggLevelchanged"/>
      <div>
        <b-button variant="primary"
                  :disabled="!aggregationValue2"
                  @click="changeAggLevel()"
                  size="sm"
                  style="margin-left: 10px;">
          <font-awesome-icon icon="exchange-alt" />
        </b-button>
      </div>
      <div class="font-weight-bold" style="margin-left: 10px;">2:</div>
      <treeselect instance-id="propertyAggregationTreeselect2"
                  ref="propertyAggregationTreeselect2"
                  v-model="aggregationValue2"
                  :clearable="true"
                  :deleteRemoves="false"
                  :backspaceRemoves="false"
                  :auto-load-root-options="false"
                  :placeholder="$t('Webportal.PropertyFilter.Placeholder')"
                  :options="aggregationItems"
                  :disableFuzzyMatching="true"
                  :multiple="false"
                  :showCount="true"
                  showCountOf="LEAF_DESCENDANTS"
                  :defaultExpandLevel="1"
                  :disable-branch-nodes="true"
                  style="max-width: 20em; margin-left: 10px;align-content: start;"
                  :disabled="!aggregationValue"
                  @input="handleTreeselectInput" 
                  @close="aggLevelchanged"/>
    </div>
    <div class="flex-container space-left space-top">
      <div class="font-weight-bold min-width">{{$t('Webportal.TopResultCount')}}:</div>
      <b-form-select class="col-xl-2 col-lg-2 col-md-3 col-sm-4" v-model="topResultCount" style="margin-left: 10px;" @change="onTopResultCountChange">
        <option :value="null">{{ $t('Webportal.Unlimited') }}</option>
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="100">100</option>
      </b-form-select>
    </div>
    <div class="flex-container space-left space-top">
      <div class="font-weight-bold min-width">{{ $t('Webportal.Kpis') }}:</div>
      <b-form-group class="col-xl-8 col-lg-8 col-md-8 col-sm-8" style="margin-left: -5px;">
        <treeselect ref="kpiTreeselect"
                    v-model="kpiValue"
                    :auto-load-root-options="false"
                    :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                    :options="kpiItems"
                    valueFormat="object"
                    :disabled="isCluster"
                    :disableFuzzyMatching="true"
                    :multiple="true"
                    :showCount="true"
                    showCountOf="LEAF_DESCENDANTS"
                    :defaultExpandLevel="1"
                    @input="handleKpiTreeselectInput"
                    @close="handleKpiTreeselectClose">
        </treeselect>
      </b-form-group>
    </div>
    <div class="dx-fieldset" v-if="gridData">
      <dx-data-grid
        id="dxdatagrid"
        ref="dxdatagrid"
        key-expr="AggLevelValue"
        :data-source="gridData"
        :show-borders="true"
        :word-wrap-enabled="true"
        :show-column-lines="false"
        :show-row-lines="true"
        :focused-row-enabled="false"
        :onCellPrepared="onCellPrepared"
        :allow-column-reordering="false"
        :row-alternation-enabled="false"
        :column-hiding-enabled="true"
        :onRowClick="onRowClick"
        :key="componentKey"
        :onRowCollapsed="onGridRowCollapsed"
        :onRowExpanded="onGridRowExpanded"
        :onAdaptiveDetailRowPreparing="onGridAdaptiveDetailRowPreparing"
        :columnAutoWidth="true"
        :onContentReady="manipulateGridDom"
        :columnMinWidth="60"
        @toolbar-preparing="onToolbarPreparing($event)">
        <dx-grouping :autoExpandAll="false">
          <dx-texts
            :groupContinuedMessage="$t('WebReport.DataGridContinuedMessage')"
            :groupContinuesMessage="$t('WebReport.DataGridContinuesMessage')"
          />
        </dx-grouping>

        <dx-load-panel :enabled="true"/>
        <dx-scrolling mode="virtual"/>
        <dx-sorting mode="none"/>

        <dx-paging :enabled="false"/>
        <!-- <dx-pager
          :show-page-size-selector="true"
          :allowed-page-sizes="[20,35,50]"
          :show-info="true" /> -->

        <dx-export :enabled="false"/>

        <dx-column
          data-field="groupValue"
          :group-index="0"
          groupCellTemplate="summaryCellTemplate"
          :sortingMethod="groupSort" />

        <dx-column :caption="$t('Webportal.Ranking')">
          <dx-column alignment="left" data-field="RankingOrder" :caption="$t('Webportal.Dashboard.Order')" :width="100"/>
        </dx-column>

        <dx-column :caption="$t('Webportal.AggregationLevel')">
          <dx-column data-field="AggLevelValue" :caption="getSelectedAggLevelName()" :width="200"/>
        </dx-column>

        <dx-column :caption="$t('Webportal.Dashboard.Checks')">
          <dx-column alignment="left" data-field="SurveyInstanceCount" caption="N" :width="100"/>
        </dx-column>

        <template v-for="(colObj, index) in getColumns()">
          <dx-column :caption="colObj.name" :key="`${index}_colname`">
            <dx-column
              :data-field="getResultColumnName(index)"
              :visible="true"
              :caption="$t('Webportal.Result')"
              :key="`${index}_result`"
              :hiding-priority="1000-index"
              cell-template="resultTemplate"
              alignment="left"
              :name="colObj.code"
            ></dx-column>
          </dx-column>
        </template>

        <dx-summary :calculate-custom-summary="calculateRow">
          <dx-group-item
            name="SurveyInstanceCount"
            :show-in-group-footer="false"
            :align-by-column="true"
            column="SurveyInstanceCount"
            summary-type="custom" />
          <template v-for="(colObj, index) in getColumns()">
            <dx-group-item
              :name="colObj.code"
              :show-in-group-footer="false"
              :align-by-column="true"
              :column="getResultColumnName(index)"
              summary-type="custom"
              :key="`${index}_resultSummary`" 
              :customizeText="getResultForAggregatedSummary"/>
          </template>
        </dx-summary>

        <dx-group-panel :visible="false"/>
        <dx-search-panel :visible="true" :highlight-case-sensitive="true"/>

        <div slot="collapseall" slot-scope="data">
          <dx-check-box
            v-model="collapseAllValue"
            :text="$t('Webportal.Dashboard.ExpandCollapse')"
            :onValueChanged="onCollapseAllChanged"
          />
        </div>

        <div slot="download" slot-scope="data">
          <div class="form-row">
            <div class="col-md-auto">
              <b-alert :show="noDataAlert"
                      dismissible
                      @dismissed="noDataAlert=0"
                      variant="warning">
                {{ $t('Webportal.NoDataFound') }}
              </b-alert>
              <b-button class="excelButton"
                        variant="primary"
                        :disabled="isExporting || !rankingdata"
                        @click="downloadRawData()"
                        size="sm"
                        v-b-tooltip.hover 
                        :title="$t('Webportal.Download')">
                <font-awesome-icon icon="file-excel" />
              </b-button>
            </div>
            <div class="col-md-1" v-bind:class="{ inlineSpinner: isExporting }"></div>
          </div>
        </div>

        <result-cell
          slot="resultTemplate"
          slot-scope="options"
          :cell-data="options"
          :thresholds="thresholds"
          :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints"
          placeholderNaN=" "
        />

        <summary-cell slot="summaryCellTemplate" slot-scope="options" :cell-data="options"/>
      </dx-data-grid>
    </div>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxExport,
  DxColumn,
  DxGrouping,
  DxGroupPanel,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxTexts,
  DxSummary,
  DxGroupItem,
  DxValueFormat,
  DxSorting,
  DxScrolling,
  DxLoadPanel
} from "devextreme-vue/data-grid";

import { DxCheckBox } from "devextreme-vue/check-box";
import ResultCell from "./ResultCell.vue";
import SummaryCell from "./SummaryCell.vue";
import EventBus from '@/services/EventBus.js';
import axios from 'axios'
import DashboardService from '@/services/DashboardService'
import AggregatedReportsService from '@/services/AggregatedReportsService';
import SurveysService from '@/services/SurveysService';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import DownloadFileMixin from '@/mixins/DownloadFileMixin';
import ThresholdColor from '@/mixins/ThresholdColor';

export default {
  name: "ResultTable",

  components: {
    DxDataGrid,
    DxExport,
    DxColumn,
    DxGrouping,
    DxGroupPanel,
    DxPager,
    DxPaging,
    DxSearchPanel,
    DxTexts,
    DxSummary,
    DxGroupItem,
    DxValueFormat,
    DxSorting,
    DxScrolling,
    DxLoadPanel,
    DxCheckBox,
    ResultCell,
    SummaryCell
  },

  mixins: [ AppConfig, DownloadFileMixin, ThresholdColor ],

  props: {
    isCluster: { default: true },
    thresholds: null,
    percentageNumberOfDecimalPoints: { type: Number, default: 2 },
    filtervalues: null,
    selection: null
  },

  watch: {
    filtervalues: {
      // immediate: true,
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal.clients, oldVal.clients))
        {
          this.rankingdata = null;
          this.aggregationValue = null;
          this.aggregationValue2 = null;
          if (!this.loadOptions()) {
            this.handleDataChanged();
          }
          this.componentKey +=1;
        }
      },
    },

    selection: {
      // immediate: true,
      handler(newVal, oldVal) {
        // console.log('RankingTable - watch - selection');
        if (!this.getSurveyKpis()) {
          this.loadData();
        }
      },
    },

    isCluster: {
      // immediate: true,
      handler(newVal, oldVal) {

        if (newVal === true) {
          this.kpiValue = [];
          this.lastKnownKpiIds = null;
        } else {
          this.kpiValue = this.kpiItems;
        }

        this.handleDataChanged();
        this.getSurveyKpis();
        this.componentKey +=1;
      },
    },
  },

  created() {
    this.loadOptions();
  },

  mounted() {
    EventBus.$on('LocalizationChanged', this.localizationChanged);
    this.intervalReference = setInterval(this.manipulateGridDom, 100);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  data() {
    return {
      componentKey: 0,
      collapseAllValue: false,
      toolTipDelay: { delay: { show: 500, hide: 100 } },
      rankingdata: null,
      gridData: null,
      aggregationItems: [],                 // Data items for aggregation treeselect
      aggregationValue: null,               // Selected aggregation item
      aggregationValue2: null,              // Selected aggregation item
      isLoading: false,
      cancelToken: null,
      isExporting: false,
      noDataAlert: 0,
      currentSummaryRowCounter: 0,
      topResultCount: null,
      summaryRows: [],
      intervalReference: undefined,
      aggLevelChangeInitiated: false,
      lastAggLevel1Value: null,
      lastAggLevel2Value: null,
      kpiItems: [],                         // Data items for kpi treeselect
      kpiValue: [],                         // Selected kpi items
      lastKnownKpiIds: null,                // remembers last known kpi selection (for selection change detection)
      lastKnownsSurveyIds: null,            // remembers last known survey selection (for selection change detection)
    };
  },

  methods: {
    localizationChanged(localizationId) {
      this.aggregationValue = null;
      this.lastKnownsSurveyIds = null; // force reload for the translations
      this.gridData = null;
      this.loadOptions();
    },

    changeAggLevel() {
      if (this.aggregationValue && this.aggregationValue2) {

        this.aggLevelChangeInitiated = true;

        const aggLvl2 = this.aggregationValue2;
        this.aggregationValue2 = this.aggregationValue;
        this.aggregationValue = aggLvl2;
      }
    },

    groupSort(val1, val2)
    {
      // only sort Total to the top
      if (val1 === ' Total') {
        return -1;
      } else if (val2 === ' Total') {
        return 1;
      }
      
      return 0; // don't sort anything
    },

    loadOptions() {
      let subItems = null;
      this.aggregationItems = this.defaultAggregationItems();

      if (this.filtervalues && this.filtervalues.customProperties) {
        if (this.filtervalues.clients && this.filtervalues.clients.length > 0) {
          subItems = {
            id: -1,
            label: this.filtervalues.clients[0].label,
            children: []
          };
        } else {
          subItems = {
            id: -1,
            label: 'CustomProperties',
            children: []
          };
        }

        for (let item of this.filtervalues.customProperties) {
          const itemToPush = {
            id: `Property^${item.id}`,
            label: item.label
          };
          subItems.children.push(itemToPush);
        }

        this.aggregationItems.push(subItems);
      }

      // load KPIs for selected surveys
      return this.getSurveyKpis();
    },

    // Get survey structure data for the structure treeselect
    // it will return whether kpis are loaded from backend or not
    getSurveyKpis() {
      let retVal = false;

      if (!this.isCluster && this.selection && this.selection.surveys && this.selection.surveys.length > 0)
      {
        // console.log('getSurveyKpis');
        // console.log(this.lastKnownsSurveyIds);
        // console.log(this.selection.surveys);
        if (this.lastKnownsSurveyIds != this.selection.surveys) {
          retVal = true;
          SurveysService.getSurveyKpis({ surveyIds: this.selection.surveys })
            .then(response => {
              this.kpiItems = response.data;
              this.lastKnownsSurveyIds = this.selection.surveys;
              this.kpiValue = this.kpiItems;
              this.loadData();
            });
        }
      };

      return retVal;
    },

    handleTreeselectInput(value, instanceId) {
      
      // prevent double backend calls, if switch button is clicked
      if (this.aggLevelChangeInitiated) {
        this.aggLevelChangeInitiated = false;
        return;
      }

      const changed = instanceId === 'propertyAggregationTreeselect' ? value !== this.lastAggLevel1Value : value !== this.lastAggLevel2Value;

      if (changed) {

        instanceId === 'propertyAggregationTreeselect' ? this.lastAggLevel1Value = value : this.lastAggLevel2Value = value;
        this.loadData();
      }
    },

    handleKpiTreeselectInput(value) {
      if (value.length === 0) {
        this.handleKpiTreeselectClose(value);
      }
    },

    handleKpiTreeselectClose(value) {
        const compareList = this.getKpiItemList(this.kpiItems, value);
        if (compareList != this.lastKnownKpiIds) {
          this.loadData();
        }
    },

    getKpiItemList(allKpiArray, selectedKpiArray) {
        // Returns an array of all KPI ids recursively for the given node
        let returnKpiIds = (obj) => {
            return _.concat(obj.id && obj.id[0] === 'K' ? obj.id.substring(1) : [],
                obj.children ? _.flatMapDeep(obj.children, returnKpiIds) : []);
        };
        let kpiIds = !_.isEqual(selectedKpiArray, allKpiArray) ? _.flatMapDeep(selectedKpiArray, returnKpiIds) : null;
        return (kpiIds && kpiIds.length > 0) ? kpiIds.join(',') : null;
    },

    loadData() {

      if (this.aggregationValue) {

        this.isLoading = true;
        const options = this.getCurrentOptions();
        const token = this.initCancelToken(this.cancelToken);
        this.gridData = null;

        // add selected kpi items to selection filter
        this.selection.kpiIds = this.getKpiItemList(this.kpiItems, this.kpiValue);

        // console.log(this.selection);
        DashboardService.getDashboardRankingData(this.selection, options, token)
          .then(response => {
            this.isLoading = false;
            this.rankingdata = response.data;
            this.lastKnownKpiIds = this.selection.kpiIds;
            this.handleDataChanged();
          })
          .catch(error => {
            console.warn('Error to receive ranking data: ' + error);
            this.isLoading = false;
          });
      }
    },

    aggLevelchanged(value, instanceId) {

      if (value && value.length === 0) { 
        this.handleTreeselectInput(value, instanceId);
      }
    },

    getCurrentOptions(forJson = true) {
      return {
        isRanking: true,
        aggregationLevel1: this.aggregationValue,
        aggregationLevel2: this.aggregationValue2,
        topResultCount: this.topResultCount,
        includeKpiPercentages: !this.isCluster,
        includeKpiPoints: false,
        includeClusterPercentages: this.isCluster,
        includeClusterPoints: false,
        includeOverallPoints: this.hasRawDataPointSelection(),
        colorizeResults: true,
        useCompactLayout: true,
        propertyUseLogicalAnd: true
      };
    },

    getCurrentFilter() {
      return {
        surveyIds: this.selection.surveys ? this.selection.surveys.split(',') : null,
        waveIds: this.selection.waves ? this.selection.waves.split(',') : null,
        locations: this.selection.locations ? this.selection.locations.split(',') : null,
        propertyIds: this.selection.customProperties? this.selection.customProperties.split(','): null,
        kpiIds: null
      }
    },

    initCancelToken(token) {
      // console.log('Dashboard - initCancelToken');

      // Cancel previous request
      if (token)
        token.cancel();
      return axios.CancelToken.source();
    },

    getColumns() {
      let retVal = [];

      if (this.rankingdata) {

        // define the total column for row summary
        retVal.push({code: 'Total', name: 'Total'});

        if (this.isCluster) {
          if (this.rankingdata.Cluster) {
            for (let clu of this.rankingdata.Cluster) {
              if (clu.Code && clu.Code.length > 0) {
                retVal.push({
                  code: clu.Code,
                  name: (clu.Name && clu.Name.length > 0) ? clu.Name : clu.Code
                });
              }
            }
          }
        } else {
          if (this.rankingdata.KPI) {
            for (let kpi of this.rankingdata.KPI) {
              if (kpi.Code && kpi.Code.length > 0) {
                retVal.push({
                  code: kpi.Code,
                  name: (kpi.Name && kpi.Name.length > 0) ? kpi.Name : kpi.Code
                });
              } else if (kpi.GroupCode) {
                retVal.push({
                  code: kpi.GroupCode,
                  name: (kpi.GroupName && kpi.GroupName.length > 0) ? kpi.GroupName : kpi.GroupCode
                });
              }
            }
          }
        }
      }

      return retVal;
    },

    getColumnNameFromCode(code) {
      let retVal = code;

      if (this.rankingdata) {
        if (this.isCluster && this.rankingdata.Cluster) {
          const item = this.rankingdata.Cluster.find(obj => obj.Code  === code);
          if (item && item.Name) {
            retVal = item.Name;
          }
        } else {
          if (this.rankingdata.KPI) {
            const item = this.rankingdata.KPI.find(obj => {
              if (obj.Code) {
                return obj.Code === code;
              } else if (obj.GroupCode) {
                return obj.GroupCode === code;
              }
              return false;
            });
            if (item && item.Name) {
              retVal = item.Name;
            } else if (item && item.GroupName) {
              retVal = item.GroupName;
            }
          }
        }
      }

      return retVal;
    },

    getResultColumnName(index) {
      return 'Result[' + index + ']';
    },

    getSelectedAggLevelName() {
      let retVal = 'N/A';
      
      const id = this.isLevel2Selected() ? this.aggregationValue2 : this.aggregationValue;

      if (id && id.startsWith('Property')) {
        const parent = this.aggregationItems.find(obj => obj.id === -1);  
        if (parent) {
          const item = parent.children.find(obj => obj.id === id);
          if (item) {
            retVal = item.label;
          }
        }
      } else {
        const item = this.aggregationItems.find(obj => obj.id === id);
        if (item) {
          retVal = item.label;
        }
      }

      return retVal;
    },

    getGridData() {
      let retVal = null;

      if (this.rankingdata && this.rankingdata.AggregatedData) {

        retVal = [];
        
        const colValues = this.isCluster
          ? this.rankingdata.Cluster
          : this.rankingdata.KPI;

        for (let i = 0; i < this.rankingdata.AggregatedData.length; i++) {
          let resultArray = [];
          
          for (let colValue of colValues) {
            let dataItem = undefined;
            
            if (this.isCluster) {
              if (this.rankingdata.AggregatedData[i].ClusterData && this.rankingdata.AggregatedData[i].ClusterData.length > 0) {
                dataItem = this.rankingdata.AggregatedData[i].ClusterData.find(item => item.Code === colValue.Code);
              }
            } else {
              if (colValue.Code) {
                if (this.rankingdata.AggregatedData[i].KpiData && this.rankingdata.AggregatedData[i].KpiData.length > 0) {
                  dataItem = this.rankingdata.AggregatedData[i].KpiData.find(item => item.Code === colValue.Code);
                }
              } else {
                if (this.rankingdata.AggregatedData[i].KpiData && this.rankingdata.AggregatedData[i].KpiData.length > 0) {
                  dataItem = this.rankingdata.AggregatedData[i].KpiData.find(item => item.GroupCode === colValue.GroupCode && !item.Code);
                }
              }
            }

            // add total value to result array
            if (resultArray.length===0) { 
              resultArray.push(this.rankingdata.AggregatedData[i].Result * 100);
            }

            if (dataItem) {
              // add cluster/kpi value to result array
              resultArray.push(dataItem.Result * 100);
            } else {
              resultArray.push(undefined);
            }
          }

          // console.log(this.getAggLevelName(i, this.rankingdata.AggregatedData[i].AggregationLevel1Value));
          // console.log(resultArray);
          if (this.rankingdata.AggregatedData[i].RankingOrder != undefined) {
            retVal.push({
              RankingOrder:           this.rankingdata.AggregatedData[i].RankingOrder,
              groupValue:             this.isLevel2Selected() ? this.getAggLevelName(this.rankingdata.AggregatedData[i].AggregationLevel1Value) : this.getSelectedAggLevelName(),
              AggLevelValue:          this.isLevel2Selected() ? this.getAggLevelName(this.rankingdata.AggregatedData[i].AggregationLevel2Value) : this.getAggLevelName(this.rankingdata.AggregatedData[i].AggregationLevel1Value),
              SurveyInstanceCount:    this.rankingdata.AggregatedData[i].SurveyInstanceCount,
              Result:                 resultArray
            });
          } else {
            if (this.isLevel2Selected() && this.rankingdata.AggregatedData[i].AggregationLevel1Value == undefined) {
              retVal.push({
                RankingOrder:           0,
                groupValue:             ' Total',
                AggLevelValue:          'Total',
                SurveyInstanceCount:    this.rankingdata.AggregatedData[i].SurveyInstanceCount,
                Result:                 resultArray
              });
            }
          }
        }
      }

      // console.log(this.rankingdata);
      // console.log(retVal);
      return retVal;
    },

    getAggLevelName(name) {
      return name ? name : "N/A";
    },

    onTopResultCountChange(value) {
      this.loadData();
    },

    onCollapseAllChanged(event) {
      const grid = this.$refs["dxdatagrid"];

      if (grid) {
        if (event.value) {
          grid.$_instance.expandAll(0);
        } else if (event.value !== undefined) {
          grid.$_instance.collapseAll(0);
        }
      }
    },

    onGridRowCollapsed(event) {
      this.checkRowCollapsedValue();
    },

    onGridRowExpanded(event) {
      this.checkRowCollapsedValue();
    },

    onGridAdaptiveDetailRowPreparing(event) {
      if (event.formOptions && event.formOptions.items) {
        event.formOptions.items = event.formOptions.items.reverse();
        for (let item of event.formOptions.items) {
          if (item.column && item.name) {
            item.column.caption = this.getColumnNameFromCode(item.name);
            //console.log(item.name + ' | Prio: ' + item.column.hidingPriority);
          }
        }
      }
    },

    checkRowCollapsedValue() {
      let state = undefined;
      const grid = this.$refs["dxdatagrid"];

      if (grid) {
        for (let row of grid.$_instance.getVisibleRows()) {
          if (row.rowType === "group") {
            if (state === undefined) {
              state = row.isExpanded;
            } else {
              if (state !== row.isExpanded) {
                // not all rows are expanded/collapsed => mixed
                state = undefined;
                break;
              }
            }
          }
        }
      }

      this.collapseAllValue = state;
    },

    onToolbarPreparing(tool) {
      tool.toolbarOptions.items.unshift({
        location: "after",
        template: "download"
      });
      tool.toolbarOptions.items.unshift({
        location: "before",
        template: "collapseall"
      });      
    },

    handleDataChanged() {
      this.gridData = this.getGridData();
      this.checkRowCollapsedValue();
    },

    onRowClick: function(e) {
      // console.log('ResultTable - onRowClick');

      if (e["groupIndex"] !== undefined && e["isExpanded"] !== undefined) {
        if (e.isExpanded) {
          e.component.collapseRow(e.key);
        } else {
          e.component.expandRow(e.key);
        }
      }
    },

    onCellPrepared: function(e) {
      // console.log('ResultTable - onCellPrepared');

      if(e.rowType == 'header') {
        e.cellElement.style.fontWeight = 'bold';
        e.cellElement.style.color = '#EEE';
        e.cellElement.style.backgroundColor = '#737178';
      } else {
        e.cellElement.style.backgroundColor = '#f7f7f7';
      }
    },

    calculateRow(options) {

      if (options.summaryProcess === undefined) {
        // console.log('calculateRow - clear - ' + options.name);
        this.currentSummaryRowCounter = -1;
        this.summaryRows = [];
      }

      if (options.summaryProcess == 'start') {
        options.totalValue = ''; 
      }

      // if (options.summaryProcess == 'calculate') {
      // this is needed if you are working with all the row values
      // in our case we have the overall values from backend which
      // aggregates correct
      // }
      
      if (options.summaryProcess == 'finalize') {

        if (options.name === 'SurveyInstanceCount') {
          this.currentSummaryRowCounter += 1;
        }

        const data = this.getAggregatedDataForSummary(this.currentSummaryRowCounter);
        // console.log('calculateRow - finalize - ' + options.name);
        // console.log(data);

        if (options.name === 'SurveyInstanceCount') {
          if (data && data.SurveyInstanceCount) {
            options.totalValue = data.SurveyInstanceCount;
          }
        } else if (options.name === 'Total') {
          if (data && data.Result) {
            options.totalValue = data.Result;
          }
        } else {
          let subData = undefined;
          // console.log(options);
          if (this.isCluster) {
            
            const cluItem = this.rankingdata.Cluster.find(item => item.Name === options.name);
            const code = (cluItem && cluItem.Code) ? cluItem.Code : options.name;

            if (data && data.ClusterData && data.ClusterData.length > 0) {
              subData = data.ClusterData.find(item => item.Code === code);
            }
          } else {

            const kpiItem = this.rankingdata.KPI.find(item => item.Name === options.name);
            const code = (kpiItem && kpiItem.Code) ? kpiItem.Code : options.name;

            if (data && data.KpiData && data.KpiData.length > 0) {
              subData = data.KpiData.find(item => item.Code ? item.Code === code : item.GroupCode === code);
            }
          }

          if (subData && subData.Result) {
            options.totalValue = subData.Result;
          }
        }
      }
    },

    getAggregatedDataForSummary(summaryIndex) {
      if (this.summaryRows.length === 0) {
        if (this.rankingdata && this.rankingdata.AggregatedData && this.rankingdata.AggregatedData.length > 0) {
          for (let i=0; i<this.rankingdata.AggregatedData.length; i++) {
            if (!this.rankingdata.AggregatedData[i].RankingOrder) {
              this.summaryRows.push(this.rankingdata.AggregatedData[i]);
            }
          }
          // console.log(this.summaryRows);
        }
      }

      return this.summaryRows.length > summaryIndex ? this.summaryRows[summaryIndex] : undefined;
    },

    getResultForAggregatedSummary(options) {
      var retVal = " ";
      const value = options.value * 100;

      if (value != null && value != undefined && !Number.isNaN(value)) {
        retVal = (value === 100 ? 
          this.$n(value, { minimumFractionDigits: 0, maximumFractionDigits: 0}) :
          this.$n(value, { minimumFractionDigits: this.percentageNumberOfDecimalPoints, maximumFractionDigits: this.percentageNumberOfDecimalPoints})) + '%';
      }
      
      return retVal;
    },

    defaultAggregationItems: function()  {
      // console.log('RankingTable - defaultAggregationItems');
      return [ { id: 'Location', label: this.$t('Webportal.LocationId') },
               { id: 'Wave', label: this.$t('Webportal.Wave') },
               { id: 'Scenario', label: this.$t('Webportal.Scenario') } ];
    },

    downloadRawData() {
      this.isExporting = true;
      this.noDataAlert = 0;

      const options = this.getCurrentOptions();
      const filter = this.getCurrentFilter();
      const token = this.initCancelToken(this.cancelToken);

      AggregatedReportsService.getRawDataAggregated(filter, options, token)
        .then(response => {
          this.isExporting = false;

          if (response.status === 204)
            this.noDataAlert = 5;
          else
            this.downloadFile(response);
        })
        .catch(error => { 
          this.isExporting = false;
          console.warn('Error to receive ranking export file: ' + error);
        });
    },
    
    isLevel2Selected() {
      return this.aggregationValue2 != undefined && this.aggregationValue2 != null && this.aggregationValue2 != '';
    },

    manipulateGridDom() {

      // prevent endless interval calls
      if (this.domManipulationRetryCounter === 30) {
        clearInterval(this.intervalReference);
        return;
      }

      if (!this.$el) {
        return;
      }

      // try to get the group item elements
      var htmlElements = this.$el.getElementsByClassName('dx-datagrid-summary-item');

      if (htmlElements && htmlElements.length > 0) {
        clearInterval(this.intervalReference);

        for (let rsElement of htmlElements) {
          const resultVal = rsElement.innerText;
          // there are result values and num of shops elements
          // we only want to do s.th. for teh result values
          if (resultVal.endsWith('%')) {

            // check if previous call already added the element(s)
            var prevElement = rsElement.previousElementSibling;
            if (prevElement && prevElement.attributes.length > 0 && prevElement.attributes[0].value === 'thresholdIndicator') {
              continue;
            }

            // enable flex row for this cell
            var pElement = rsElement.parentElement;
            // var attr = document.createAttribute("style");
            // attr.value = 'display: flex; flex-direction: row; text-align: left;'
            // pElement.setAttributeNode(attr);

            // add indicator element
            var indicatorElement = document.createElement("div");
            var attr = document.createAttribute("id");
            attr.value = 'thresholdIndicator';
            indicatorElement.setAttributeNode(attr);
            attr = document.createAttribute("style");
            attr.value = 'background-color: ' + this.getThresholdColor(this.thresholds, resultVal, '#ff201c') + '; margin-top: 2px; margin-right: 5px; width: 15px; height: 15px; float: left;';
            indicatorElement.setAttributeNode(attr);

            pElement.insertBefore(indicatorElement, rsElement);
          }
        }
      }

      this.checkRowCollapsedValue();
    },
  },

  computed: {
  }

};
</script>

<style scoped>

.excelButton {
  background-color: #107c41;
  border-color: #107c41;
}

.flex-container {
  display: flex;
  align-items: center;
}

.space-top {
  margin-top: 15px;
}

.space-left {
  margin-left: 15px;
}

.min-width {
  min-width: 175px;
}

.group-switch {
  margin-bottom: 1px;
  margin-right: 5px;
  color: #333;
  font-weight: normal;
  font-size: 14px;
  font-family: "Helvetica Neue", "Segoe UI", Helvetica, Verdana, sans-serif;
}

.card-body {
  padding: 0px;
  margin-bottom: 0.5rem;
}

.dx-fieldset {
  margin: 1rem;
}

#tasks {
  min-height: 150px;
}

#tasks .dx-treelist-rowsview td {
  vertical-align: middle;
}

.pitem {
  text-align: center;
  height: 40px;
  /* width: 300px; */
  margin-left: 347px;
  margin-right: 228px;
}
</style>
